import { ChevronRightIcon, HashIcon, TagsIcon } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { COLOR_TOKEN_CLASSES } from "@/constants/tagColors";
import { type JournalFilter, useJournalFilterContext } from "@/contexts/JournalFilterContext";
import { useTags } from "@/hooks/useTagQueries";
import { cn } from "@/lib/utils";
import { useTranslate } from "@/utils/i18n";

interface Props {
  readonly?: boolean;
  tagCount: Record<string, number>;
}

const TagsSection = (props: Props) => {
  const t = useTranslate();
  const { getFiltersByFactor, addFilter, removeFilter } = useJournalFilterContext();
  const { data: tagsFromAPI = [] } = useTags({ enabled: !props.readonly });

  // Build tag lookup map from API: key is tagId, value contains displayName and colorToken
  const tagLookup = useMemo(() => {
    const map = new Map<string, { displayName: string; colorToken: string }>();
    tagsFromAPI.forEach((tag) => {
      if (tag.tagId) {
        map.set(tag.tagId, { displayName: tag.displayName, colorToken: tag.colorToken });
      }
    });
    return map;
  }, [tagsFromAPI]);

  // Process tags with API data for colors (keep original array format for tree mode)
  const tags = useMemo(() => {
    return Object.entries(props.tagCount)
      .map<[string, number]>(([tagName, count]) => [tagName, count])
      .sort(([a], [b]) => a.localeCompare(b))
      .sort(([, a], [, b]) => b - a);
  }, [props.tagCount]);

  // Get color class for a tag id
  const getColorClass = (tagId: string): string => {
    const tagInfo = tagLookup.get(tagId);
    const colorToken = tagInfo?.colorToken;
    return colorToken ? COLOR_TOKEN_CLASSES[colorToken] || COLOR_TOKEN_CLASSES.primary : "";
  };

  // Get display name for a tag id
  const getDisplayName = (tagId: string): string => {
    const tagInfo = tagLookup.get(tagId);
    return tagInfo?.displayName || tagId;
  };

  const handleTagClick = (tag: string) => {
    const isActive = getFiltersByFactor("tagSearch").some((filter: JournalFilter) => filter.value === tag);
    if (isActive) {
      removeFilter((f: JournalFilter) => f.factor === "tagSearch" && f.value === tag);
    } else {
      // Remove all existing tag filters first, then add the new one
      removeFilter((f: JournalFilter) => f.factor === "tagSearch");
      addFilter({
        factor: "tagSearch",
        value: tag,
      });
    }
  };

  return (
    <div className="w-full flex flex-col justify-start items-start h-auto shrink-0 flex-nowrap hide-scrollbar">
      {/* Section header with icon */}
      <div className="flex items-center justify-between w-full gap-1 mb-2">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/5 dark:to-indigo-950/5 border border-blue-200/50 dark:border-blue-800/10 flex items-center justify-center">
            <TagsIcon className="w-3 h-3 text-blue-600 dark:text-blue-400" />
          </div>
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("common.tags")}</span>
        </div>
        {tags.length > 0 && (
          <Link
            to="/setting#tag-management"
            className="group relative px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-300 flex items-center gap-1"
          >
            {/* Background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-indigo-500/10 dark:from-blue-500/5 dark:to-indigo-500/5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Border gradient */}
            <div className="absolute inset-0 rounded-full border border-blue-500/20 dark:border-blue-500/20 group-hover:border-blue-500/40 dark:group-hover:border-blue-500/30 transition-colors duration-300" />

            {/* Content */}
            <span className="relative text-muted-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300">
              {t("tickers_section.view_all")}
            </span>
            <ChevronRightIcon className="relative w-3 h-3 text-muted-foreground/60 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-all duration-300 group-hover:translate-x-0.5" />
          </Link>
        )}
      </div>
      {tags.length > 0 ? (
        <div className="w-full flex flex-row justify-start items-center relative flex-wrap gap-x-2 gap-y-1.5">
          {tags.map(([tagId, count]) => {
            const displayName = getDisplayName(tagId);
            const isActive = getFiltersByFactor("tagSearch").some((filter: JournalFilter) => filter.value === tagId);
            const colorClass = getColorClass(tagId);
            return (
              <div
                key={tagId}
                className={cn(
                  "shrink-0 w-auto max-w-full text-sm rounded-md leading-6 flex flex-row justify-start items-center select-none cursor-pointer transition-colors",
                  "hover:opacity-80",
                  !colorClass && (isActive ? "text-primary" : "text-muted-foreground"),
                )}
                onClick={() => handleTagClick(tagId)}
              >
                <HashIcon className="w-4 h-auto shrink-0 text-current" />
                <div className={cn("inline-flex flex-nowrap ml-0.5 gap-0.5 max-w-[calc(100%-16px)] px-1.5 py-0 rounded", colorClass)}>
                  <span className={cn("truncate", isActive && !colorClass ? "font-medium" : "")}>{displayName}</span>
                  <span className="opacity-60 shrink-0">({count})</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !props.readonly && (
          <div className="p-2 border border-dashed rounded-md flex flex-row justify-start items-start gap-2 text-muted-foreground">
            <TagsIcon className="w-5 h-5 shrink-0 text-current opacity-60" />
            <p className="text-sm leading-snug italic">{t("tag.create-tags-guide")}</p>
          </div>
        )
      )}
    </div>
  );
};

export default TagsSection;
